import { useState } from "react";
import { ArrowLeft, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { GroupedMoneyInput, Money } from "@workspace/accounting/components";
import { CheckOperation } from "@workspace/operations/check-operation";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { ButtonGroup } from "@workspace/ui/components/button-group";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import { Checkbox } from "@workspace/ui/components/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@workspace/ui/components/select";
import { Separator } from "@workspace/ui/components/separator";
import { Spinner } from "@workspace/ui/components/spinner";
import { useAppForm } from "@workspace/ui/components/tanstack-form";
import { Textarea } from "@workspace/ui/components/textarea";
import { cn } from "@workspace/ui/lib/utils";

import { eposApi } from "../../api";
import type { Order, OrderItem } from "../../types";
import { useEposContext } from "../epos-context";
import { refundLinesTotal } from "./refund-summary";
import { RefundSubtotalRows } from "./refund-summary-rows";
import { refundableTip } from "./refund-tip";

const REFUND_REASONS = [
	{ value: "customer-dissatisfied", label: "Customer Dissatisfied" },
	{ value: "wrong-order", label: "Wrong Order" },
	{ value: "quality-issue", label: "Quality Issue" },
	{ value: "overcharged", label: "Overcharged" },
	{ value: "duplicate-charge", label: "Duplicate Charge" },
	{ value: "cancelled-order", label: "Cancelled Order" },
	{ value: "other", label: "Other" },
] as const;

const REFUND_METHODS = [
	{ value: "cash", label: "Cash" },
	{ value: "card", label: "Card" },
	{ value: "store-credit", label: "Store Credit" },
] as const;

type RefundType = "full" | "partial";
type RefundItemSelection = Record<string, number>; // itemId -> quantity to refund

interface RefundViewProps {
	order: Order;
	onClose: () => void;
}

export function RefundView({ order, onClose }: RefundViewProps) {
	const { tenant, venueId, data, operations } = useEposContext();

	const queryClient = useQueryClient();

	// UI state (not form data)
	const [refundType, setRefundType] = useState<RefundType | null>(null);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	// HA-858 (S4): "Refund the tip too?" on a full refund, all of it or none.
	const [refundTip, setRefundTip] = useState(false);

	// Calculate refundable balance
	const refundableBalance = order.refundableBalance?.value ?? order.total.value;
	const currency = order.currency;

	// Get max refundable quantity for an item
	const getMaxRefundableQty = (item: OrderItem) => {
		const alreadyRefunded = item.refundedQuantity ?? 0;
		return item.quantity - alreadyRefunded;
	};

	const form = useAppForm({
		defaultValues: {
			selectedItems: {} as RefundItemSelection,
			customAmount: 0,
			useCustomAmount: false,
			refundMethod: "",
			refundReason: "",
			refundNotes: "",
		},
		validators: {
			onSubmit: ({ value }) => {
				const errors: Record<string, string> = {};
				if (!value.refundMethod) {
					errors.refundMethod = "Refund method is required";
				}
				if (!value.refundReason) {
					errors.refundReason = "Refund reason is required";
				}
				const total = calculateRefundTotalFromValues(value);
				if (total <= 0) {
					errors.customAmount = "Refund amount must be greater than 0";
				}
				if (total > refundableBalance) {
					errors.customAmount = "Amount exceeds refundable balance";
				}
				if (refundType === "partial" && !value.useCustomAmount) {
					const hasSelectedItems = Object.values(value.selectedItems).some(
						(qty) => qty > 0,
					);
					if (!hasSelectedItems) {
						errors.selectedItems = "Select at least one item to refund";
					}
				}
				return Object.keys(errors).length > 0 ? errors : undefined;
			},
		},
	});

	// Subscribe to form values for calculations
	const formValues = useStore(form.store, (state) => state.values);
	const {
		selectedItems,
		customAmount,
		useCustomAmount,
		refundMethod,
		refundReason,
		refundNotes,
	} = formValues;

	const refundMutation = useMutation({
		...eposApi.createRefundOptions(tenant, venueId, order.id),
		onSuccess(result) {
			if (!result.success) return;

			// HA-858 (S4, planning's addendum): the endpoint answers { order, refund }, not the till's EPOS read, so this used to
			// write nothing; the EPOS read is refetched instead.
			void queryClient.invalidateQueries({
				queryKey: [tenant, venueId, "epos"],
			});

			// Check if this was a card refund and if it failed
			if (result.refund && result.refund.status === "Failed") {
				toast.error("Card refund failed", {
					description:
						result.refund.errorMessage ||
						"The refund could not be processed. Please try again.",
				});
				setShowConfirmDialog(false);
				return;
			}

			toast.success("Refund processed successfully");
			onClose();
		},
		onError(error) {
			toast.error("Failed to process refund", {
				description: error instanceof Error ? error.message : "Unknown error",
			});
		},
	});

	// HA-849 PR 3: refunds are priced at the rate the order was sold at, not the venue's current one.
	const orderTaxRate = order.taxRate ?? data.settings.taxRate;

	// Calculate selected items total
	const calculateItemsTotal = (items: RefundItemSelection) => {
		let total = 0;
		for (const [itemId, qty] of Object.entries(items)) {
			const item = order.items.find((i) => i.id === itemId);
			if (item && qty > 0) {
				// Base price
				let itemPrice = item.price.value;
				// Add option price impacts
				if (item.selectedOptions) {
					for (const group of item.selectedOptions) {
						for (const opt of group.selectedItems) {
							itemPrice += opt.priceImpact.value;
						}
					}
				}
				total += itemPrice * qty;
			}
		}
		// HA-849 PR 3: plus tax at the order's rate, as the server prices a refund of lines.
		return refundLinesTotal(total, orderTaxRate);
	};

	// Calculate refund total based on type and selection
	const calculateRefundTotalFromValues = (values: typeof formValues) => {
		if (refundType === "full") {
			return refundableBalance;
		}
		if (values.useCustomAmount) {
			return values.customAmount;
		}
		return calculateItemsTotal(values.selectedItems);
	};

	const refundTotal = calculateRefundTotalFromValues(formValues);
	// HA-858 (S4): the tip a full refund can hand back, as the server honours it (0: none to offer).
	const tipOffer =
		refundType === "full" ? refundableTip(order.payments, refundMethod) : 0;
	const tipRefunded = refundTip && tipOffer > 0 ? tipOffer : 0;

	// Validation
	const isValidRefund = () => {
		if (!refundType) return false;
		if (!refundMethod) return false;
		if (!refundReason) return false;
		if (refundTotal <= 0) return false;
		if (refundTotal > refundableBalance) return false;
		if (refundType === "partial" && !useCustomAmount) {
			const hasSelectedItems = Object.values(selectedItems).some(
				(qty) => qty > 0,
			);
			if (!hasSelectedItems) return false;
		}
		return true;
	};

	// Handle item selection toggle
	const toggleItemSelection = (itemId: string, maxQty: number) => {
		const current = selectedItems[itemId] ?? 0;
		if (current > 0) {
			// Deselect
			const { [itemId]: _, ...rest } = selectedItems;
			form.setFieldValue("selectedItems", rest);
		} else {
			// Select with max qty
			form.setFieldValue("selectedItems", {
				...selectedItems,
				[itemId]: maxQty,
			});
		}
	};

	// Handle item quantity change
	const updateItemQuantity = (itemId: string, qty: number, maxQty: number) => {
		const validQty = Math.max(0, Math.min(qty, maxQty));
		if (validQty === 0) {
			const { [itemId]: _, ...rest } = selectedItems;
			form.setFieldValue("selectedItems", rest);
		} else {
			form.setFieldValue("selectedItems", {
				...selectedItems,
				[itemId]: validQty,
			});
		}
	};

	// Handle full refund selection
	const handleFullRefund = () => {
		setRefundType("full");
		form.setFieldValue("selectedItems", {});
		form.setFieldValue("useCustomAmount", false);
		form.setFieldValue("customAmount", 0);
	};

	// Handle partial refund selection
	const handlePartialRefund = () => {
		setRefundType("partial");
	};

	// Submit refund
	const handleSubmitRefund = () => {
		const items = Object.entries(selectedItems)
			.filter(([_, qty]) => qty > 0)
			.map(([itemId, quantity]) => ({ itemId, quantity }));

		refundMutation.mutate({
			type: refundType === "full" ? "Full" : "Partial",
			refundTip: tipRefunded > 0,
			method: refundMethod,
			reason: refundReason,
			notes: refundNotes || null,
			customAmount: useCustomAmount
				? { value: customAmount, currency, version: 2 as const }
				: null,
			items:
				refundType === "full"
					? order.items.map((item) => ({
							itemId: item.id,
							quantity: getMaxRefundableQty(item),
						}))
					: items,
		});
	};

	// Back button handler
	const handleBack = () => {
		if (showConfirmDialog) {
			setShowConfirmDialog(false);
		} else if (refundType) {
			setRefundType(null);
			form.reset();
		} else {
			onClose();
		}
	};

	return (
		<CheckOperation operation="staff-terminal-refund" operations={operations}>
			<Card className="flex max-h-[90vh] flex-col border-0 shadow-none">
				<CardHeader className="shrink-0 pb-3">
					<div className="flex items-center justify-between">
						<div>
							<CardTitle className="flex items-center gap-2 text-lg">
								<RotateCcw className="size-5" />
								Process Refund
							</CardTitle>
							<CardDescription>Order #{order.id}</CardDescription>
						</div>
						<Badge variant="outline" className="text-sm">
							Refundable:{" "}
							<Money value={refundableBalance} currency={currency} />
						</Badge>
					</div>
				</CardHeader>

				<ScrollArea className="min-h-0 flex-1 border-t">
					<CardContent className="p-4">
						{/* Step 1: Select refund type */}
						{!refundType && (
							<div className="space-y-4">
								<h3 className="font-medium">Select Refund Type</h3>
								<div className="grid grid-cols-2 gap-4">
									<Button
										variant="outline"
										className="flex h-24 flex-col items-center justify-center gap-2"
										onClick={handleFullRefund}
									>
										<RotateCcw className="size-8" />
										<span>Full Refund</span>
										<span className="text-xs text-muted-foreground">
											<Money value={refundableBalance} currency={currency} />
										</span>
									</Button>
									<Button
										variant="outline"
										className="flex h-24 flex-col items-center justify-center gap-2"
										onClick={handlePartialRefund}
									>
										<Check className="size-8" />
										<span>Partial Refund</span>
										<span className="text-xs text-muted-foreground">
											Select items or amount
										</span>
									</Button>
								</div>

								{/* Order items preview */}
								<div className="mt-6">
									<h4 className="mb-2 text-sm font-medium text-muted-foreground">
										Order Items
									</h4>
									<div className="space-y-2">
										{order.items.map((item) => {
											const refundedQty = item.refundedQuantity ?? 0;
											const isFullyRefunded = refundedQty >= item.quantity;

											return (
												<div
													key={item.id}
													className={cn(
														"flex items-center justify-between rounded-md border p-3",
														isFullyRefunded && "bg-muted opacity-60",
													)}
												>
													<div>
														<span className="font-medium">{item.name}</span>
														<span className="ml-2 text-muted-foreground">
															x{item.quantity}
														</span>
														{refundedQty > 0 && (
															<Badge variant="secondary" className="ml-2">
																{refundedQty} refunded
															</Badge>
														)}
													</div>
													<Money {...item.price} />
												</div>
											);
										})}
									</div>
								</div>
							</div>
						)}

						{/* Step 2: Partial refund - item selection or custom amount */}
						{refundType === "partial" && (
							<div className="space-y-4">
								<h3 className="font-medium">Select Items or Enter Amount</h3>

								<form.AppField name="useCustomAmount">
									{(field) => (
										<div className="flex items-center gap-4">
											<Button
												variant={!field.state.value ? "default" : "outline"}
												size="sm"
												onClick={() => field.handleChange(false)}
											>
												Select Items
											</Button>
											<Button
												variant={field.state.value ? "default" : "outline"}
												size="sm"
												onClick={() => field.handleChange(true)}
											>
												Custom Amount
											</Button>
										</div>
									)}
								</form.AppField>

								{!useCustomAmount ? (
									<div className="space-y-2">
										{order.items.map((item) => {
											const maxQty = getMaxRefundableQty(item);
											const selectedQty = selectedItems[item.id] ?? 0;
											const isDisabled = maxQty === 0;

											return (
												<div
													key={item.id}
													className={cn(
														"flex items-center gap-3 rounded-md border p-3",
														isDisabled && "bg-muted opacity-60",
													)}
												>
													<Checkbox
														checked={selectedQty > 0}
														disabled={isDisabled}
														onCheckedChange={() =>
															toggleItemSelection(item.id, maxQty)
														}
														className="size-6"
													/>
													<div className="flex-1">
														<span className="font-medium">{item.name}</span>
														{item.refundedQuantity &&
															item.refundedQuantity > 0 && (
																<Badge variant="secondary" className="ml-2">
																	{item.refundedQuantity} already refunded
																</Badge>
															)}
													</div>
													{selectedQty > 0 && maxQty > 1 && (
														<div className="flex items-center gap-2">
															<Label className="text-sm">Qty:</Label>
															<Input
																type="number"
																min={1}
																max={maxQty}
																value={selectedQty}
																onChange={(e) =>
																	updateItemQuantity(
																		item.id,
																		parseInt(e.target.value) || 0,
																		maxQty,
																	)
																}
																className="w-16 text-center"
															/>
															<span className="text-sm text-muted-foreground">
																/ {maxQty}
															</span>
														</div>
													)}
													<Money {...item.price} />
												</div>
											);
										})}
									</div>
								) : (
									<form.AppField name="customAmount">
										{(field) => (
											<field.FormItem>
												<field.FormLabel>Refund Amount</field.FormLabel>
												<field.FormControl>
													<GroupedMoneyInput
														value={field.state.value}
														onChange={(value) => field.handleChange(value)}
														currency={currency}
														className="text-lg"
													/>
												</field.FormControl>
												<p className="text-sm text-muted-foreground">
													Maximum refundable:{" "}
													<Money
														value={refundableBalance}
														currency={currency}
													/>
												</p>
												{field.state.value > refundableBalance && (
													<p className="text-sm text-destructive">
														Amount exceeds refundable balance
													</p>
												)}
												<field.FormMessage />
											</field.FormItem>
										)}
									</form.AppField>
								)}
							</div>
						)}

						{/* Step 3: Full refund or partial with selections - method & reason */}
						{refundType &&
							(refundType === "full" ||
								(useCustomAmount
									? customAmount > 0
									: Object.values(selectedItems).some((q) => q > 0))) && (
								<div className="mt-6 space-y-4">
									<Separator />

									<div className="space-y-4">
										<form.AppField name="refundMethod">
											{(field) => (
												<field.FormItem>
													<field.FormLabel>Refund Method *</field.FormLabel>
													<field.FormControl>
														<Select
															value={field.state.value}
															onValueChange={(value) =>
																field.handleChange(value)
															}
														>
															<SelectTrigger className="h-12" name={field.name}>
																<SelectValue placeholder="Select refund method" />
															</SelectTrigger>
															<SelectContent>
																{REFUND_METHODS.map((method) => (
																	<SelectItem
																		key={method.value}
																		value={method.value}
																		className="h-12"
																	>
																		{method.label}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</field.FormControl>
													<field.FormMessage />
												</field.FormItem>
											)}
										</form.AppField>

										<form.AppField name="refundReason">
											{(field) => (
												<field.FormItem>
													<field.FormLabel>Reason *</field.FormLabel>
													<field.FormControl>
														<Select
															value={field.state.value}
															onValueChange={(value) =>
																field.handleChange(value)
															}
														>
															<SelectTrigger className="h-12" name={field.name}>
																<SelectValue placeholder="Select reason" />
															</SelectTrigger>
															<SelectContent>
																{REFUND_REASONS.map((reason) => (
																	<SelectItem
																		key={reason.value}
																		value={reason.value}
																		className="h-12"
																	>
																		{reason.label}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</field.FormControl>
													<field.FormMessage />
												</field.FormItem>
											)}
										</form.AppField>

										<form.AppField name="refundNotes">
											{(field) => (
												<field.FormItem>
													<field.FormLabel>Notes (Optional)</field.FormLabel>
													<field.FormControl>
														<Textarea
															value={field.state.value}
															onChange={(e) =>
																field.handleChange(e.target.value)
															}
															placeholder="Additional notes..."
															className="min-h-[80px]"
														/>
													</field.FormControl>
													<field.FormMessage />
												</field.FormItem>
											)}
										</form.AppField>
									</div>

									{/* Summary */}
									<div className="rounded-md border bg-muted/50 p-4">
										<h4 className="mb-3 font-medium">Refund Summary</h4>
										<div className="space-y-2 text-sm">
											<RefundSubtotalRows
												total={refundTotal}
												taxRatePercent={orderTaxRate}
												currency={currency}
											/>
											<Separator />
											<div className="flex justify-between font-medium text-base">
												<span>Total Refund</span>
												<Money value={refundTotal} currency={currency} />
											</div>
										</div>
									</div>
								</div>
							)}
					</CardContent>
				</ScrollArea>

				<CardFooter className="flex flex-col gap-2 border-t pt-4">
					<ButtonGroup className="w-full">
						<Button
							variant="outline"
							size="lg"
							className="h-14 flex-1"
							onClick={handleBack}
						>
							<ArrowLeft className="mr-2 size-5" />
							Back
						</Button>
						{refundType && (
							<Button
								variant="default"
								size="lg"
								className="h-14 flex-1"
								disabled={!isValidRefund() || refundMutation.isPending}
								onClick={() => setShowConfirmDialog(true)}
							>
								{refundMutation.isPending
									? "Processing..."
									: "Review & Confirm"}
							</Button>
						)}
					</ButtonGroup>
				</CardFooter>

				{/* Confirmation Dialog */}
				<Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
					<DialogContent className="sm:max-w-md">
						<DialogHeader>
							<DialogTitle>Confirm Refund</DialogTitle>
							<DialogDescription>
								Please review the refund details before confirming.
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-4">
							<div className="rounded-md border p-4">
								<h4 className="mb-3 font-medium">Refund Details</h4>
								<div className="space-y-2 text-sm">
									<div className="flex justify-between">
										<span className="text-muted-foreground">Order</span>
										<span>#{order.id}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Type</span>
										<span className="capitalize">{refundType} Refund</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Method</span>
										<span>
											{
												REFUND_METHODS.find((m) => m.value === refundMethod)
													?.label
											}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Reason</span>
										<span>
											{
												REFUND_REASONS.find((r) => r.value === refundReason)
													?.label
											}
										</span>
									</div>
									{refundNotes && (
										<div className="flex justify-between">
											<span className="text-muted-foreground">Notes</span>
											<span className="max-w-[200px] truncate">
												{refundNotes}
											</span>
										</div>
									)}
									<Separator className="my-2" />
									<RefundSubtotalRows
										total={refundTotal}
										taxRatePercent={orderTaxRate}
										currency={currency}
										taxLabel="Tax"
										labelClassName="text-muted-foreground"
									/>
									{tipRefunded > 0 && (
										<div className="flex justify-between">
											<span className="text-muted-foreground">Tip</span>
											<Money value={tipRefunded} currency={currency} />
										</div>
									)}
									<Separator className="my-2" />
									<div className="flex justify-between font-medium text-base">
										<span>Total</span>
										<Money
											value={refundTotal + tipRefunded}
											currency={currency}
										/>
									</div>
								</div>
							</div>

							{tipOffer > 0 && (
								<div className="flex items-center gap-2 rounded-md border p-4">
									<Checkbox
										id="refund-tip"
										checked={refundTip}
										onCheckedChange={(checked) =>
											setRefundTip(checked === true)
										}
									/>
									<Label htmlFor="refund-tip">
										{`Refund the £${(tipOffer / 100).toFixed(2)} tip too?`}
									</Label>
								</div>
							)}

							{refundType === "partial" && !useCustomAmount && (
								<div className="rounded-md border p-4">
									<h4 className="mb-2 font-medium">Items to Refund</h4>
									<div className="space-y-1 text-sm">
										{Object.entries(selectedItems)
											.filter(([_, qty]) => qty > 0)
											.map(([itemId, qty]) => {
												const item = order.items.find((i) => i.id === itemId);
												return item ? (
													<div key={itemId} className="flex justify-between">
														<span>
															{item.name} x{qty}
														</span>
														<Money {...item.price} />
													</div>
												) : null;
											})}
									</div>
								</div>
							)}
						</div>

						{refundMethod === "card" && (
							<div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
								<strong>Card Refund:</strong> This will process through Stripe
								and may take a few seconds.
							</div>
						)}

						<DialogFooter className="flex justify-between gap-2">
							<Button
								variant="outline"
								onClick={() => setShowConfirmDialog(false)}
								disabled={refundMutation.isPending}
							>
								Cancel
							</Button>
							<Button
								variant="default"
								onClick={handleSubmitRefund}
								disabled={refundMutation.isPending}
								className="min-w-[120px]"
							>
								{refundMutation.isPending && <Spinner />}
								Confirm Refund
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</Card>
		</CheckOperation>
	);
}
